import {createApi} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { NotificationActions } from './notificationSlice';

var urlPrefix = '';

switch(process.env.ENVIRONMENT_SETTINGS) {
case 'local':
    urlPrefix = 'http://localhost:8000';
    break;
case 'staging':
    urlPrefix = 'https://staging.docsihealth.com';
    break;
case 'demo':
    urlPrefix = 'https://demo.docsihealth.com';
    break;
case 'production':
    urlPrefix = 'https://api.docsihealth.com';
    break;
}

const buildBaseQuery = () => {
    const query = fetchBaseQuery({
        baseUrl: `${urlPrefix}/api`,
        prepareHeaders: (headers) => {
            const token = localStorage.getItem('token');
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }
            headers.set('accept', 'application/json');
            return headers;
        },
        credentials: 'include'
    });
    return async (args, api, extraOptions) => {
        const { error, data } = await query(args, api, extraOptions);
        if (error) {
            console.error('API Error:', error);
            if (error.status === 401) {
                localStorage.removeItem('token');
                window.location.href = '/login';
            }
            return { error: { status: error.status, data: error.data } };
        }
        return { data };
    }
} 

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: buildBaseQuery(),
    tagTypes: ['Case', 'Organization', 'Procedure', 'Card', 'Physician', 'Inventory', 'User', 'UserCards', 'Hospital', 'HospitalUsers', 'Campaign', 'CampaignPreview', 'CampaignDrafts', 'CardOwner', 'CommentCategory', 'AuxiliaryItemCategory', 'Vendor', 'Item', 'ProposedMatch', 'Conversation', 'OrgUsers', 'ImplementerDrafts', 'Reviewer', 'TransformationJobs', 'SourceFileUploads', 'AugmentationFileUploads', 'FileHeaders', 'FileMetadata', 'ItemUsageStats', 'Contract'],
    endpoints: (builder) => ({
        getOrganization: builder.query({
            query : (organizationId) => `/organizations/${organizationId}`
        }),
        getOrganizations: builder.query({
            query : () => '/organizations',
            providesTags: (result = [], error, arg) => [
                'Organization',
                ...result.map(({ id }) => ({ type: 'Organization', id }))
            ]
        }),
        getHospital: builder.query({
            query : (hospitalId) => `/hospitals/${hospitalId}`
        }),
        getHospitals: builder.query({
            query : () => '/hospitals',
            providesTags: (result = [], error, arg) => [
                'Hospital',
                ...result.map(({ id }) => ({ type: 'Hospital', id }))
            ]
        }),
        getHospitalsInOrganization: builder.query({
            query : (organizationId) => `/organizations/${organizationId}/hospitals`,
            providesTags: (result = [], error, arg) => [
                'Hospital',
                ...result.map(({ id }) => ({ type: 'Hospital', id }))
            ]
        }),

        generateLink : builder.mutation({
            query : ({campaign, cardOwner }) => ({
                url: `/campaigns/${campaign}/cardOwners/${cardOwner}/dashboardLink`,
                method: 'POST',
            })
        }),

        createHospital: builder.mutation({
            query: ({ organizationId, data }) => ({
                url: `/organizations/${organizationId}/hospitals`,
                method: 'POST',
                body: data
            }),
            async onQueryStarted({organizationId, data}, { dispatch, queryFulfilled }) {
                try {
                    const { data: newHospital } = await queryFulfilled
                    dispatch(
                        apiSlice.util.updateQueryData('getOrganizations', undefined, draft => {
                            const orgIndex = draft.findIndex((org) => org.id === organizationId);
                            draft[orgIndex].hospitals.push(newHospital);
                        })
                    )
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not create hospital at this time. Please try again later.'
                    }));
                }
            }
        }),

        updateHospital: builder.mutation({
            query: ({ hospitalId, formData }) => ({
                url: `/hospitals/${hospitalId}`,
                method: 'PATCH',
                body: formData
            }),

            async onQueryStarted({ hospitalId, data }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedHospital } = await queryFulfilled;
                    const result = dispatch(
                        apiSlice.util.updateQueryData('getHospitals', hospitalId.toString(), draft => {
                            Object.assign(draft, updatedHospital);
                        })
                    )
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not update hospital at this time. Please try again later.'
                    }));
                }
            }
        }),

        createOrganization: builder.mutation({
            query: ({ data }) => ({
                url: '/organizations',
                method: 'POST',
                body: data
            }),
            async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
                try {
                    const { data: newOrg } = await queryFulfilled;
                    dispatch(
                        apiSlice.util.updateQueryData('getOrganizations', undefined, draft => {
                            draft.push(newOrg);
                        })
                    )
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not create organization at this time. Please try again later.'
                    }));
                }
            }
        }),

        updateOrganization: builder.mutation({
            query: ({ organizationId, data }) => ({
                url: `/organizations/${organizationId}`,
                method: 'PATCH',
                body: data
            }),
            async onQueryStarted({ organizationId, data }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedOrg } = await queryFulfilled;
                    dispatch(
                        apiSlice.util.updateQueryData('getOrganizations', undefined, draft => {
                            const orgIndex = draft.findIndex((org) => org.id === organizationId);
                            draft[orgIndex] = updatedOrg;
                        })
                    )
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not update organization at this time. Please try again later.'
                    }));
                }
            }
        }),

        getHospitalProcedures: builder.query({
            query: (hospitalId) => `/hospitals/${hospitalId}/procedures`,
            providesTags: (result, error, arg) => [
                'Procedure',
                ...result.map(({ id }) => [{ type: 'Procedure', id }])
            ]
        }),
        getProcedureComparisons: builder.query({
            query: ({ procedureId }) => `/procedures/${procedureId}/comparisons`,
        }),
        changeUserHospital: builder.mutation({
            query: ({ user, hospital_id }) => ({
                url: `/users/${user.id}`,
                method: 'PATCH',
                body: {
                    current_hospital_id: hospital_id
                }
            })
        }),
        getIngestionToken: builder.query({
            query : () => '/ingestionToken'
        }),
        getItemServiceToken: builder.query({
            query: () => '/itemServiceToken'
        }),
        getHospitalReviewers: builder.query({
            query: (hospitalId) => `/hospitals/${hospitalId}/reviewers`,
            providesTags: ['Reviewers']
        }),
        bulkCreateUserAssignments: builder.mutation({
            query: ({ assignments }) => ({
                url: '/reviewAssignments/bulk',
                method: 'POST',
                body: {
                    assignments: assignments.map(({ cardId, reviewerId }) => ({
                        card_id: cardId,
                        assigned_user_id: reviewerId
                    }))
                }
            }),
        }),
        bulkDeleteUserAssignments: builder.mutation({
            query: ({ assignments }) => ({
                url: '/reviewAssignments/bulk',
                method: 'DELETE',
                body: {
                    assignments: assignments.map(({ cardId, reviewerId }) => ({
                        card_id: cardId,
                        assigned_user_id: reviewerId
                    }))
                }
            }),
        }),
        getRecommendationBatches: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/recommendationGenerationBatches`,
            providesTags: ['RecommendationBatches']
        }),
        createRecommendationBatch: builder.mutation({
            query: ({ data }) => ({
                url: '/recommendationGenerationBatches',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['RecommendationBatches']
        }),
        getPacks: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/packs`,
        }),
        getPackSummary: builder.query({
            query: (packId) => `/packs/${packId}/summary`,
        }),
        updatePackReviews: builder.mutation({
            query: ({ cardOwnerId, packReviewId, reviewData }) => ({
                url: `/cardOwners/${cardOwnerId}/packReviews/${packReviewId}`,
                method: 'PATCH',
                body: reviewData
            })
        }),
        logUserAction: builder.mutation({
            query: ({ action, data }) => ({
                url: '/userActions',
                method: 'POST',
                body: {
                    action: action,
                    data: data
                }
            })
        }),
        login: builder.mutation({
            query: (data) => ({
                url: '/passwordLogin',
                method: 'POST',
                body: data
            }),
        }),
        generateItemDetails: builder.query({
            query: (itemId) => `/items/${itemId}/generativeItemDetails`,
        }),
        sendGenerativeItemDetailVote: builder.mutation({
            query: ({ detailId, vote }) => ({
                url: `/generativeItemDetails/${detailId}/votes`,
                method: 'PUT',
                body: {
                    vote: vote
                }
            }),
        }),        
        agentChat: builder.mutation({
            query: (data) => ({
                url: '/agent/chat',
                method: 'POST',
                body: {
                    message: data.message,
                    user_id: data.userId,
                    ...(data.conversationId && { conversation_id: data.conversationId })
                }
            }),
        }),
        expireCardOwnerTokens: builder.mutation({
            query: (cardOwnerId) => ({
                url: `/cardOwners/${cardOwnerId}/activeTokens`,
                method: 'DELETE',
            }),
            async onQueryStarted(cardOwnerId, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Tokens expired successfully.'
                    }));
                } catch {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Could not expire tokens at this time. Please try again later.'
                    }));
                }
            },
            invalidatesTags: ['Hospital', 'Physician']
        })
   }),
});

export const { 
    useGetOrganizationQuery,
    useGetHospitalQuery,
    useGetHospitalsQuery,
    useGetOrganizationsQuery,
    useGetHospitalsInOrganizationQuery,
    useUpdateHospitalMutation,
    useGenerateLinkMutation,
    useCreateHospitalMutation,
    useCreateOrganizationMutation,
    useUpdateOrganizationMutation,
    useGetHospitalProceduresQuery,
    useGetProcedureComparisonsQuery,
    useChangeUserHospitalMutation,
    useGetIngestionTokenQuery,
    useGetItemServiceTokenQuery,
    useGetHospitalReviewersQuery,
    useBulkCreateUserAssignmentsMutation,
    useBulkDeleteUserAssignmentsMutation,
    useGetRecommendationBatchesQuery,
    useCreateRecommendationBatchMutation,
    useGetPacksQuery,
    useGetPackSummaryQuery,
    useUpdatePackReviewsMutation,
    useLogUserActionMutation,
    useLoginMutation,
    useGenerateItemDetailsQuery,
    useSendGenerativeItemDetailVoteMutation,
    useAgentChatMutation,
    useExpireCardOwnerTokensMutation,
} = apiSlice
